import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import HistoryIcon from '@mui/icons-material/History';
import MemoryIcon from '@mui/icons-material/Memory';
import BugReportIcon from '@mui/icons-material/BugReport';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ArticleIcon from '@mui/icons-material/Article';
import RouterIcon from '@mui/icons-material/Router';
import TerminalIcon from '@mui/icons-material/Terminal';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import GridViewIcon from '@mui/icons-material/GridView';
import PaletteIcon from '@mui/icons-material/Palette';
import LogoutIcon from '@mui/icons-material/Logout';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMe, useMyPermissions, useOperatorBranding, useOperatorLogoUrl } from '../../api/queries';
import { clearSessionToken } from '../../auth/useAccessToken';
import { ChangePasswordDialog } from '../auth/ChangePasswordDialog';
import { ImpersonateOperatorDialog } from './ImpersonateOperatorDialog';
import { Manage2faDialog } from '../auth/Manage2faDialog';
import armsLogoUrl from '../../assets/arms-logo.png';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SecurityUpdateIcon from '@mui/icons-material/SecurityUpdate';
import KeyIcon from '@mui/icons-material/Key';
import { useStopImpersonation, useApiVersion } from '../../api/queries';
import { setSessionToken } from '../../auth/useAccessToken';
import { fmtDateTime } from '../../utils/format';

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED = 64;

// requiresArms hides the entry for operator-tenant users. The API would
// 401 them anyway, but a hidden link is a better UX than a dead one.
type NavItem = { to: string; label: string; icon: ReactNode; requiresArms?: boolean; requiresPermission?: string };
type NavSection = { heading: string; items: NavItem[] };

const sections: NavSection[] = [
  {
    heading: 'Operations',
    items: [
      { to: '/home',            label: 'Home',                  icon: <HomeIcon /> },
      { to: '/operators',       label: 'Operators',             icon: <BusinessIcon />, requiresArms: true },
      { to: '/sites',           label: 'Sites',                 icon: <StorefrontIcon /> },
      { to: '/machines',        label: 'Machines',              icon: <VideogameAssetIcon /> },
      // Revenue dashboard (portfolio/site/machine drill-down). Hidden — not
      // disabled — for users without the permission (UP-MVP wave-1 task 8).
      { to: '/revenue',         label: 'Revenue',               icon: <AssessmentIcon />, requiresPermission: 'revenue.dashboard.view' },
      // Operators legitimately see their OWN commissioned HATs (RLS scopes
      // the rows to devices whose SC is bound to their operator); blank /
      // uncommissioned HATs stay ARMS-only. No requiresArms — like Sites /
      // Machines, the row-level filter does the scoping.
      { to: '/iot-devices',     label: 'IoT devices (HATs)',    icon: <RouterIcon /> },
      // Site controllers: consolidated operator-aware surface (Fleet folded
      // in). Operators see + self-serve their OWN commissioned SCs — incl.
      // the void flow — scoped by RLS + endpoint ownership checks; the
      // uncommissioned pool + voided rows stay ARMS-only. No requiresArms —
      // the row-level filter does the scoping, like Sites / Machines / HATs.
      { to: '/site-controllers', label: 'Site controllers',     icon: <RouterIcon /> },
      // Recorded remote-shell session log + playback. Gated on the same
      // permission that opens a session (operators see their own SCs via RLS).
      { to: '/site-controllers/terminal-sessions', label: 'Terminal sessions', icon: <TerminalIcon />, requiresPermission: 'provisioning.site-controllers.terminal' },
      // SC SSH device-cert recovery approvals (two-person control). Only
      // holders of cert-reissue-approve see the action-item surface.
      { to: '/site-controllers/recovery-approvals', label: 'Recovery approvals', icon: <VpnKeyIcon />, requiresPermission: 'provisioning.site-controllers.cert-reissue-approve' },
      // SC SSH Phase-2 CA rotation control surface (operational + commissioning
      // CA rotate/retire, "still on v1" uptake). Visible to …ssh holders; the
      // commissioning actions are Platform-only inside the page.
      { to: '/site-controllers/ca-rotation', label: 'CA rotation', icon: <VpnKeyIcon />, requiresPermission: 'provisioning.site-controllers.ssh' },
      { to: '/suppliers',       label: 'Machine Vendors',       icon: <LocalShippingIcon />, requiresArms: true },
      { to: '/approvals',       label: 'Approvals',             icon: <HowToVoteIcon /> },
      { to: '/audit',           label: 'Audit log',             icon: <HistoryIcon /> },
      { to: '/issues',          label: 'Issues',                icon: <BugReportIcon /> },
    ],
  },
  // V2 §15.1 — ARMS Staff group (ACES platform-tier roles only).
  // Order: header templates first (most-edited from inside this group),
  // then the artefact lists (machine QR / SC QR / SCs / releases).
  // Distinct icons per entry — no two QR-shaped glyphs side by side.
  // QR Code header templates lead the section because they're the
  // upstream design dependency for every batch printed below.
  {
    heading: 'ARMS Staff',
    items: [
      { to: '/stickers/header-templates', label: 'QR Code header templates', icon: <ArticleIcon />,        requiresArms: true },
      { to: '/factory/qr-batches',        label: 'SC QR batches',            icon: <QrCodeScannerIcon />,  requiresArms: true },
      { to: '/factory/iot-qr-batches',    label: 'IoT (HAT) QR batches',     icon: <QrCodeScannerIcon />,  requiresArms: true },
      { to: '/factory/machine-qr-batches', label: 'Machine QR batches',      icon: <QrCodeScannerIcon />,  requiresArms: true },
      { to: '/releases/catalogue',        label: 'Release catalogue',        icon: <LayersIcon />,         requiresArms: true },
      { to: '/machine-comm-analyser', label: 'Machine Comm Analyser', icon: <MemoryIcon />, requiresArms: true, requiresPermission: 'provisioning.sas-analyzer' },
    ],
  },
  {
    // Cash desk (stage 1 — line-of-sight AFT). Per-site surfaces; RLS +
    // endpoint tenancy scope operators to their own sites. The Activities
    // page joins this section in W10a when its endpoints land.
    heading: 'Cash desk',
    items: [
      { to: '/cashdesk/terminals', label: 'Terminals', icon: <PointOfSaleIcon />, requiresPermission: 'cashdesk.terminals.manage' },
      { to: '/cashdesk/options',   label: 'Options',   icon: <TuneIcon />,        requiresPermission: 'cashdesk.options.manage' },
      { to: '/cashdesk/floor-layout', label: 'Floor layout', icon: <GridViewIcon />, requiresPermission: 'cashdesk.layout.manage' },
      { to: '/cashdesk/activities', label: 'Activities', icon: <ReceiptLongIcon />, requiresPermission: 'cashdesk.activity.read' },
    ],
  },
  {
    heading: 'Access control',
    items: [
      { to: '/users',       label: 'Users',       icon: <PeopleIcon /> },
      { to: '/roles',       label: 'Roles',       icon: <SecurityIcon /> },
      { to: '/permissions', label: 'Permissions', icon: <VerifiedUserIcon /> },
    ],
  },
  {
    heading: 'Settings',
    items: [
      { to: '/settings/appearance', label: 'Appearance',         icon: <PaletteIcon /> },
      { to: '/settings/system',     label: 'System settings',    icon: <TuneIcon /> },
      { to: '/operator/branding',   label: 'Operator branding',  icon: <SettingsIcon /> },
    ],
  },
];

// #163 — page-title context. AppShell mounts once at the layout level so
// the sidebar's scroll position survives navigation. Pages publish their
// title via usePageTitle() instead of passing it as a prop.
const PageTitleContext = createContext<((title: string) => void) | null>(null);

export function usePageTitle(title: string): void {
  const setTitle = useContext(PageTitleContext);
  useEffect(() => {
    if (setTitle) setTitle(title);
  }, [setTitle, title]);
}

export type AppShellProps = {
  /** Legacy per-page title prop. Prefer `usePageTitle(...)` from inside
   *  the page component so AppShell can stay mounted as a layout. */
  title?: string;
  /** When omitted, AppShell renders `<Outlet/>` (layout mode). When
   *  provided (legacy), renders the explicit children. */
  children?: ReactNode;
};

/**
 * Shared chrome for every authenticated page — collapsible sidebar +
 * topbar with a user menu. Replaces the per-page `<AppBar>` blocks.
 *
 * Stage 1.2 baseline; deeper navigation (breadcrumb, search, notification
 * bell) lands in S1.4 / S1.5 alongside the features that need them.
 */
export function AppShell({ title, children }: AppShellProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  // #163 — layout-mode title comes from the page via PageTitleContext.
  // Legacy `title` prop wins if a caller still passes it explicitly.
  const [pageTitle, setPageTitle] = useState<string>('');
  const effectiveTitle = title ?? pageTitle;
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [twofaMode, setTwofaMode] = useState<'reset' | 'regenerate' | null>(null);
  const stopImp = useStopImpersonation();
  const navigate = useNavigate();
  const me = useMe();
  const perms = useMyPermissions();
  const canViewAudit = perms.data?.codes?.includes('audit.view') ?? false;
  const location = useLocation();
  const theme = useTheme();

  // Effective operator during impersonation is the impersonated one; the
  // bearer token scopes RLS correctly so the same id works for branding +
  // logo lookups without special-casing.
  const operatorId = me.data?.operator_id ?? null;
  const operatorBranding = useOperatorBranding(operatorId);
  const operatorLogoUrl = useOperatorLogoUrl(operatorId, operatorBranding.data?.has_logo);

  const width = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  // Build identity for the version chip. Portal bundle stamped at vite
  // build time (__BUILD_*__ globals); API stamp comes live from
  // /api/v1/version. Showing both lets the user spot a half-deploy
  // (SHAs would diverge). Times render in viewer's local TZ.
  const fmtBuildTime = (s: string) => s === 'dev' ? 'dev' : fmtDateTime(s);
  const portalVersion = `v${__BUILD_VERSION__}`;
  const portalSha     = __BUILD_SHA__.slice(0, 7);
  const portalTime    = fmtBuildTime(__BUILD_TIME__);
  const apiVersion    = useApiVersion();
  const apiVer        = `v${apiVersion.data?.version ?? '?'}`;
  const apiSha        = (apiVersion.data?.sha ?? 'dev').slice(0, 7);
  const apiTime       = fmtBuildTime(apiVersion.data?.buildTime ?? 'dev');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar */}
      <Box sx={{
        width,
        flexShrink: 0,
        transition: 'width 0.18s ease',
        bgcolor: theme.palette.mode === 'dark' ? '#0c1222' : '#1f3b6f',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
      }}>
        <Toolbar sx={{ minHeight: 64, px: 2, gap: 1 }}>
          <IconButton onClick={() => setCollapsed((v) => !v)} sx={{ color: '#fff' }} size="small">
            <MenuIcon />
          </IconButton>
          {!collapsed && (
            // Operator users see their operator's logo (fetched WITH the
            // bearer token as an object-URL — a plain <img> can't auth);
            // ACES + supplier users see the ARMS wordmark PNG. The operator
            // logo only renders once its blob URL is ready, so there's no
            // broken-image flash and no onError hide hack.
            operatorLogoUrl ? (
              <Box
                component="img"
                src={operatorLogoUrl}
                alt="Operator logo"
                sx={{ height: 36, width: 'auto', maxWidth: 150, objectFit: 'contain' }}
              />
            ) : !operatorId ? (
              <Box
                component="img"
                src={armsLogoUrl}
                alt="ARMS"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                sx={{ height: 36, width: 'auto', maxWidth: 150, objectFit: 'contain' }}
              />
            ) : null
          )}
        </Toolbar>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />

        <Box sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
          {sections.map((sec) => (
            <Box key={sec.heading} sx={{ mb: 1 }}>
              {!collapsed && (
                <Typography variant="overline" sx={{ pl: 2, color: 'rgba(255,255,255,0.55)' }}>
                  {sec.heading}
                </Typography>
              )}
              <List dense>
                {sec.items
                  .filter((item) => (!item.requiresArms || me.data?.is_arms)
                    && (!item.requiresPermission || (perms.data?.codes?.includes(item.requiresPermission) ?? false)))
                  .map((item) => {
                  const active = location.pathname === item.to ||
                    (item.to !== '/home' && location.pathname.startsWith(item.to));
                  return (
                    <ListItem key={item.to} disablePadding>
                      <Tooltip title={collapsed ? item.label : ''} placement="right">
                        <ListItemButton
                          component={Link}
                          to={item.to}
                          sx={{
                            mx: 1,
                            borderRadius: 1,
                            color: '#fff',
                            bgcolor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                            // Active item shows a 4px stripe in the user's accent
                            // colour — most visible place the accent applies.
                            borderLeft: active ? 4 : 0,
                            borderLeftColor: 'secondary.main',
                            pl: active ? 1 : 2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                          }}
                        >
                          <ListItemIcon sx={{ color: '#fff', minWidth: collapsed ? 0 : 36 }}>
                            {item.icon}
                          </ListItemIcon>
                          {!collapsed && <ListItemText primary={item.label} />}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        {/* Build identity — compact two-liner at the bottom of the
            sidebar. Portal vs. API on separate lines so a divergent
            SHA (= half deploy) is visible at a glance. */}
        <Box sx={{
          borderTop: '1px solid rgba(255,255,255,0.12)',
          px: collapsed ? 0.5 : 1.5,
          py: 0.75,
          fontSize: 10.5,
          fontFamily: 'monospace',
          color: 'rgba(255,255,255,0.65)',
          lineHeight: 1.35,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {collapsed ? (
            <Tooltip
              placement="right"
              title={
                <Box sx={{ fontSize: 12, lineHeight: 1.5, fontFamily: 'monospace' }}>
                  <div>Portal {portalVersion} · {portalSha} · {portalTime}</div>
                  <div>API    {apiVer} · {apiSha} · {apiTime}</div>
                  <div style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
                    Click a SHA to view the commit on GitHub.
                  </div>
                </Box>
              }
            >
              <Box sx={{ textAlign: 'center', cursor: 'help' }}>·</Box>
            </Tooltip>
          ) : (
            <Stack spacing={0.25}>
              <Box>Portal {portalVersion} · <CommitLink sha={portalSha} /> · {portalTime}</Box>
              <Box>API {apiVer} · <CommitLink sha={apiSha} /> · {apiTime}</Box>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Main column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {me.data?.impersonated_operator_id && (
          // Persistent, unmistakable banner. Anything an ACES user does
          // while this is up applies to the impersonated operator's
          // tenancy. Wired here (and not in each page) so it's literally
          // impossible to navigate to a screen where you forget what
          // mode you're in.
          <Box sx={{
            bgcolor: 'warning.main', color: 'warning.contrastText',
            px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <SupervisorAccountIcon fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
              You are impersonating{' '}
              <strong>
                {me.data.impersonated_operator_display_name
                  ?? me.data.impersonated_operator_code
                  ?? `${me.data.impersonated_operator_id?.slice(0, 8)}…`}
              </strong>
              {' '}as{' '}
              <strong>
                {me.data.impersonated_role_display_name
                  ?? me.data.impersonated_role_code
                  ?? 'role'}
              </strong>.
              Every action you take is recorded against this operator with you (<strong>{me.data.username}</strong>) as the actor.
            </Typography>
            <Button
              size="small" color="inherit" variant="outlined"
              startIcon={<StopCircleIcon />} disabled={stopImp.isPending}
              onClick={async () => {
                const result = await stopImp.mutateAsync();
                setSessionToken(result.token);
                window.location.assign('/home');
              }}
            >
              Stop impersonating
            </Button>
          </Box>
        )}
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Toolbar sx={{ minHeight: 64, gap: 2 }}>
            {operatorLogoUrl && (
              <Box
                component="img"
                src={operatorLogoUrl}
                alt="Operator logo"
                sx={{ height: 36, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
              />
            )}
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>
              {effectiveTitle}
            </Typography>
            <Tooltip title={me.data?.display_name ?? me.data?.username ?? 'Account'}>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size="small">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {(me.data?.display_name ?? me.data?.username ?? '?')[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor !== null}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Stack spacing={0} sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {me.data?.display_name ?? me.data?.username ?? '—'}
                  </Typography>
                  {me.data?.email && (
                    <Typography variant="caption" color="text.secondary">
                      {me.data.email}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setMenuAnchor(null); navigate('/settings/appearance'); }}>
                <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
                Appearance
              </MenuItem>
              {canViewAudit && me.data?.user_id && (
                <MenuItem onClick={() => {
                  setMenuAnchor(null);
                  navigate(`/users/${me.data!.user_id}/login-audit`);
                }}>
                  <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                  My audit history
                </MenuItem>
              )}
              <MenuItem onClick={() => { setMenuAnchor(null); setChangePwdOpen(true); }}>
                <ListItemIcon><VpnKeyIcon fontSize="small" /></ListItemIcon>
                Change password
              </MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); setTwofaMode('reset'); }}>
                <ListItemIcon><SecurityUpdateIcon fontSize="small" /></ListItemIcon>
                Reset 2FA
              </MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); setTwofaMode('regenerate'); }}>
                <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
                Regenerate backup codes
              </MenuItem>
              {me.data?.is_arms && !me.data?.impersonated_operator_id && (
                <MenuItem onClick={() => { setMenuAnchor(null); setImpersonateOpen(true); }}>
                  <ListItemIcon><SupervisorAccountIcon fontSize="small" /></ListItemIcon>
                  Impersonate operator…
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={() => {
                setMenuAnchor(null);
                clearSessionToken();
                navigate('/login');
              }}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <ChangePasswordDialog
          open={changePwdOpen}
          onClose={() => setChangePwdOpen(false)}
        />

        <ImpersonateOperatorDialog
          open={impersonateOpen}
          onClose={() => setImpersonateOpen(false)}
        />

        <Manage2faDialog
          open={twofaMode !== null}
          mode={twofaMode ?? 'reset'}
          onClose={() => setTwofaMode(null)}
        />

        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          <PageTitleContext.Provider value={setPageTitle}>
            {children ?? <Outlet />}
          </PageTitleContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Inline anchor that turns a 7-char short SHA into a clickable link to
 * the GitHub commit page. "dev" / "dev+dirty" stay as plain text — no
 * commit to point at on those builds. Tooltip on hover spells out the
 * destination so the user knows what they're about to open.
 */
function CommitLink({ sha }: { sha: string }) {
  if (!sha || sha.startsWith('dev')) {
    return <span>{sha || '?'}</span>;
  }
  // Strip a +dirty suffix if present — the SHA itself is what GitHub
  // resolves; the dirty marker just means "uncommitted changes were
  // present when this was built."
  const clean = sha.split('+')[0];
  const url = `https://github.com/nigelwitty64/Route/commit/${clean}`;
  return (
    <Tooltip title="View this commit on GitHub" placement="top">
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: 'inherit',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textDecorationColor: 'rgba(255,255,255,0.4)',
          '&:hover': { color: '#fff', textDecorationColor: '#fff' },
        }}
      >
        {sha}
      </Box>
    </Tooltip>
  );
}
